'use client';
import React, { useState } from 'react';

import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Link from 'next/link';

const faqs = [
  {
    category: 'Ordering',
    items: [
      {
        q: 'How do I place an order on mdeliva?',
        a: 'Simply browse our app or website, pick your items from any of our 1,200+ local vendors, add them to your cart, and checkout. You can pay by card, mobile money, wallet, or cash on delivery.',
      },
      {
        q: 'Can I order without creating an account?',
        a: 'Yes! No account is needed for your first order. We make it easy to get started — just enter your delivery address and go.',
      },
      {
        q: 'What can I order through mdeliva?',
        a: 'You can order food from restaurants, fresh groceries, pharmacy essentials, and everyday household items — all delivered fast to your door.',
      },
      {
        q: 'How long does delivery take?',
        a: 'Our average delivery time is 18 minutes. Actual time depends on your location and the vendor. You can track your rider live on the map.',
      },
    ],
  },
  {
    category: 'Vendors & Sellers',
    items: [
      {
        q: 'How do I list my business on mdeliva?',
        a: 'Reach out to us at info@enterpriset56.com with your business details. Our team will onboard you quickly and help you start receiving orders.',
      },
      {
        q: 'What types of businesses can sell on mdeliva?',
        a: 'Restaurants, food stalls, grocery stores, pharmacies, and any retail business that can fulfil local delivery orders are welcome.',
      },
      {
        q: 'Are there fees for vendors?',
        a: 'We operate on a commission model. Contact us at info@enterpriset56.com for current rates and partnership terms.',
      },
    ],
  },
  {
    category: 'Riders & Delivery',
    items: [
      {
        q: 'How do I become a delivery rider?',
        a: 'We are actively recruiting riders. Email info@enterpriset56.com with the subject "Rider Application" and we will get back to you with next steps.',
      },
      {
        q: 'What are the working hours for riders?',
        a: 'Riders enjoy fully flexible hours — work when you want, earn daily payouts, and unlock bonus rewards for high-performance shifts.',
      },
    ],
  },
  {
    category: 'Payments & Refunds',
    items: [
      {
        q: 'What payment methods are accepted?',
        a: 'We accept card payments, mobile money, in-app wallet balance, and cash on delivery depending on your location.',
      },
      {
        q: 'What happens if my order is wrong or missing items?',
        a: 'Contact us immediately at info@enterpriset56.com or through the app. We will investigate and issue a refund or replacement as quickly as possible.',
      },
    ],
  },
];

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className={`border rounded-xl overflow-hidden transition-all duration-300 ${open ? 'border-[#f97316]/40 bg-[#1a0e00]/60' : 'border-[#1e1e1e] bg-[#111111]'}`}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 text-left gap-4"
      >
        <span className={`font-semibold text-sm md:text-base transition-colors duration-200 ${open ? 'text-[#f97316]' : 'text-white'}`}>
          {q}
        </span>
        <span
          className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-300 ${open ? 'bg-[#f97316] rotate-45' : 'bg-[#1e1e1e]'}`}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M6 1v10M1 6h10" stroke="white" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
        </span>
      </button>
      {open && (
        <div className="px-6 pb-5">
          <p className="text-gray-400 text-sm leading-relaxed">{a}</p>
        </div>
      )}
    </div>
  );
}

export default function FAQPage() {
  return (
    <main className="min-h-screen bg-[#0a0a0a] text-white overflow-x-hidden">
      <Navbar />

      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        {/* Background grid */}
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: 'linear-gradient(rgba(249,115,22,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(249,115,22,0.03) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }} />
        {/* Glow */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-[#f97316]/6 blur-[120px] rounded-full pointer-events-none" />
        <div className="absolute bottom-20 right-10 w-[200px] h-[200px] bg-[#f5c518]/5 blur-[80px] rounded-full pointer-events-none" />

        {/* Floating dots */}
        <div className="absolute top-36 left-16 w-2 h-2 rounded-full bg-[#f97316]/50 animate-pulse pointer-events-none" />
        <div className="absolute top-52 right-24 w-1.5 h-1.5 rounded-full bg-[#f5c518]/60 animate-pulse pointer-events-none" style={{ animationDelay: '0.7s' }} />
        <div className="absolute bottom-48 left-24 w-1 h-1 rounded-full bg-[#f97316]/40 animate-pulse pointer-events-none" style={{ animationDelay: '1.3s' }} />

        <div className="relative z-10 max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-14">
            <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1a0e00] border border-[#3d2000] text-[#f97316] text-sm font-medium mb-6">
              <span>❓</span>
              <span>Frequently asked questions</span>
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Got{' '}
              <span style={{
                background: 'linear-gradient(135deg, #f97316, #f5c518)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}>
                questions?
              </span>
            </h1>
            <p className="text-gray-400 text-lg max-w-md mx-auto">
              Everything you need to know about ordering, selling, and delivering with mdeliva.
            </p>
          </div>

          {/* FAQ Categories */}
          <div className="space-y-10">
            {faqs.map((section) => (
              <div key={section.category}>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-xs font-bold uppercase tracking-widest text-[#f97316] bg-[#1a0e00] border border-[#3d2000] px-3 py-1 rounded-full">
                    {section.category}
                  </span>
                  <div className="flex-1 h-px bg-[#1e1e1e]" />
                </div>
                <div className="space-y-3">
                  {section.items.map((item) => (
                    <FAQItem key={item.q} q={item.q} a={item.a} />
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Still have questions CTA */}
          <div className="mt-14 bg-[#111111] border border-[#1e1e1e] rounded-2xl p-8 text-center">
            <div className="text-3xl mb-3">💬</div>
            <h3 className="text-white font-bold text-xl mb-2">Still have questions?</h3>
            <p className="text-gray-400 text-sm mb-6 max-w-sm mx-auto">
              Can&apos;t find what you&apos;re looking for? Our team is happy to help — reach out directly.
            </p>
            <Link
              href="/contact"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-semibold text-black text-base transition-all duration-200 hover:scale-105 hover:brightness-110"
              style={{ background: 'linear-gradient(135deg, #f5c518, #f97316)' }}
            >
              <span>✉️</span>
              <span>Contact us</span>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
